import { useState, useCallback } from 'react';
import socketService from '@/lib/socket';
export interface FileUploadItem {
  file: File;
  status: 'waiting' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}
/**
 * useFileUpload Hook
 * 管理文件上传状态和逻辑
 */
export function useFileUpload() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<FileUploadItem[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [autoCloseCountdown, setAutoCloseCountdown] = useState(0);
  /**
   * 上传文件到项目
   */
  const uploadProjectFiles = useCallback(
    async (projectId: string, files: File[]) => {
      if (!projectId || files.length === 0) return;
      // Initialize file upload items
      const initialUploadFiles: FileUploadItem[] = files.map(file => ({
        file,
        status: 'waiting',
        progress: 0,
      }));
      setUploadFiles(initialUploadFiles);
      setShowUploadModal(true);
      setIsUploadingFiles(true);
      try {
        // Upload files one by one to track individual progress
        const results = await Promise.allSettled(
          files.map(async (file, index) => {
            // Update status to uploading
            setUploadFiles(prev =>
              prev.map((item, i) =>
                i === index
                  ? { ...item, status: 'uploading', progress: 0 }
                  : item
              )
            );
            try {
              const formData = new FormData();
              formData.append('files', file);
              // Get socket ID for upload tracking
              const socketId: any = socketService.getSocketId();
              formData.append('sid', socketId);
              const uploadUrl = `/api/projects/${projectId}/files`;
              // Create a promise that resolves when upload is complete
              const uploadPromise = new Promise<Response>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                // Set up progress tracking
                xhr.upload.addEventListener('progress', event => {
                  if (event.lengthComputable) {
                    const progress = Math.round(
                      (event.loaded / event.total) * 100
                    );
                    setUploadFiles(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, progress } : item
                      )
                    );
                  }
                });
                // Handle successful upload
                xhr.addEventListener('load', () => {
                  if (xhr.status >= 200 && xhr.status < 300) {
                    const response = new Response(xhr.responseText, {
                      status: xhr.status,
                      statusText: xhr.statusText,
                      headers: new Headers({
                        'Content-Type':
                          xhr.getResponseHeader('Content-Type') ||
                          'application/json',
                      }),
                    });
                    resolve(response);
                  } else {
                    console.error(
                      `Upload failed for ${file.name}:`,
                      xhr.status,
                      xhr.statusText
                    );
                    reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
                  }
                });
                // Handle upload errors
                xhr.addEventListener('error', () => {
                  console.error(`Network error uploading ${file.name}`);
                  reject(new Error('Network error'));
                });
                xhr.addEventListener('abort', () => {
                  console.error(`Upload aborted for ${file.name}`);
                  reject(new Error('Upload aborted'));
                });
                // Open request and send
                xhr.open('POST', uploadUrl);
                // Add auth token
                const token = localStorage.getItem('user-token');
                if (token) {
                  xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                }
                xhr.send(formData);
              });
              const response = await uploadPromise;
              // Mark as completed
              setUploadFiles(prev =>
                prev.map((item, i) =>
                  i === index
                    ? { ...item, status: 'success', progress: 100 }
                    : item
                )
              );
              return response;
            } catch (error) {
              console.error(`Error uploading ${file.name}:`, error);
              // Mark as error
              setUploadFiles(prev =>
                prev.map((item, i) =>
                  i === index
                    ? {
                        ...item,
                        status: 'error',
                        error:
                          error instanceof Error
                            ? error.message
                            : 'Unknown error',
                      }
                    : item
                )
              );
              throw error;
            }
          })
        );
        // Check if all uploads succeeded
        const allSuccess = results.every(
          result => result.status === 'fulfilled'
        );
        if (allSuccess) {
          // Start auto-close countdown
          let countdown = 3;
          setAutoCloseCountdown(countdown);
          const countdownInterval = setInterval(() => {
            countdown--;
            setAutoCloseCountdown(countdown);
            if (countdown <= 0) {
              clearInterval(countdownInterval);
              setShowUploadModal(false);
              setUploadFiles([]);
              setAutoCloseCountdown(0);
            }
          }, 1000);
        } else {
          console.error('Some files failed to upload');
        }
      } catch (error) {
        console.error('Upload process error:', error);
      } finally {
        setIsUploadingFiles(false);
      }
    },
    []
  );
  /**
   * 关闭上传模态框
   */
  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setAutoCloseCountdown(0);
  }, []);
  /**
   * 移除单个文件
   */
  const removeUploadFile = useCallback((index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  }, []);
  return {
    // State
    showUploadModal,
    uploadFiles,
    isUploadingFiles,
    autoCloseCountdown,
    // Actions
    uploadProjectFiles,
    closeUploadModal,
    removeUploadFile,
  };
}
